import { PlatformDataSourceQuery } from '@data/_generated-api/platform-api/models/platform-data-source-query';
import { PlatformFilterOption } from '@data/_generated-api/platform-api/models/platform-filter-option';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { DsQueryFilterOperator } from '@common/result/data-source/enum/ds-query-filter-operator';
import { FilterField, SortField } from '@common/querying/types';

export interface HdChartQueryInput {
  page: number;       // 1-based (UI)
  pageSize: number;
  search?: string;    // vrije tekst op naam
  /** Kolomsorteringen vanuit de tabel (key = DTO-property). Default = nieuwst aangemaakt eerst. */
  sortFields?: SortField<PlatformChartListItemDto>[];
  /** Kolomfilters vanuit de tabel (key = DTO-property). */
  filterFields?: FilterField<PlatformChartListItemDto>[];
  /** Filterbalk op de gepromoveerde HD-eigenschappen (canonieke keys, AND tussen properties, OR binnen een property). */
  propertyFilters?: HdChartPropertyFilters;
}

export interface HdChartPropertyFilters {
  hdTypes?: string[];
  hdAuthorities?: string[];
  hdDefinitions?: string[];
  hdProfiles?: string[];
  /**
   * Center-statefilters: backend-kolom (bv. `HdCenterSacral`) → gewenste
   * `PlatformHdCenterState`-waarde. Meerdere centers combineren als AND.
   */
  centerStates?: Record<string, number>;
}

/** Filterbalk-lijst → Chart-entity-property (de center-states mappen apart, zie buildFilterOptions). */
const PROPERTY_FILTER_BACKEND_PROPERTY: Record<string, string> = {
  hdTypes: 'HdType',
  hdAuthorities: 'HdAuthority',
  hdDefinitions: 'HdDefinition',
  hdProfiles: 'HdProfile',
};

/**
 * Default-sortering. Paging is bij dit datasource-endpoint **enkel toegestaan met een
 * expliciete sort** (ORM-restrictie: zonder ORDER BY geeft SQL geen gegarandeerde volgorde).
 * `CreatedOn DESC` = recentst aangemaakte charts eerst. `LastModifiedOn` is ongeschikt als
 * default omdat dat null is voor charts die nog nooit gewijzigd zijn; de gebruiker kan via de
 * grid alsnog op "Gewijzigd" sorteren.
 */
export const HD_CHART_DEFAULT_SORT: { propertyName: string; direction: 'ASC' | 'DESC' } = {
  propertyName: 'CreatedOn',
  direction: 'DESC',
};

/**
 * De datasource-query draait backend op de `Chart` EF-entity (Dynamic LINQ), dus de
 * propertyName moet een entity-property zijn — NIET een DTO-only veld. `TypeDescription`
 * bestaat bv. enkel op de DTO; de entity sorteert/filtert op de enum `Type`.
 */
const BACKEND_PROPERTY_BY_COLUMN_KEY: Record<string, string> = {
  name: 'Name',
  typeDescription: 'Type',
  birthDateUtc: 'BirthDateUtc',
  birthCity: 'BirthCity',
  birthCountry: 'BirthCountry',
  visibility: 'Visibility',
  tags: 'Tags',
  createdOn: 'CreatedOn',
  lastModifiedOn: 'LastModifiedOn',
  // Gepromoveerde HD-eigenschappen (sorteerbaar; filteren loopt via de filterbalk).
  hdType: 'HdType',
  hdAuthority: 'HdAuthority',
  hdDefinition: 'HdDefinition',
  hdProfile: 'HdProfile',
};

/** Kolomfilter-key → operator + backend-property. Onbekende keys worden genegeerd (defensief). */
const FILTER_OPERATOR_BY_COLUMN_KEY: Record<string, DsQueryFilterOperator> = {
  name: DsQueryFilterOperator.StringContains,
  typeDescription: DsQueryFilterOperator.IntEquals,
  birthCountry: DsQueryFilterOperator.StringContains,
};

/**
 * Bouwt de PlatformDataSourceQuery voor /charts/query/datasource.
 *
 * Backend-contract (Egitura.Core DataSourceQuery):
 *  - `currentPageIndex` is **1-based** en MOET >= 1 zijn (0 → 400 BadRequest).
 *  - `pageSize` moet 1..100 zijn.
 *  - `filterOptionGroup`: ofwel weglaten/null, ofwel met minstens 1 entry;
 *    `logicalOperator` MOET leeg/null zijn (enkel single-group ondersteund).
 *    Alle filterOptions binnen de group worden AND-gecombineerd.
 *  - `propertyName` matcht de PascalCase-properties van de Chart-entity (bv. `Name`).
 */
export function buildChartDataSourceQuery(input: HdChartQueryInput): PlatformDataSourceQuery {
  const filterOptions = buildFilterOptions(input);
  return {
    pageOption: {
      currentPageIndex: Math.max(1, Math.floor(input.page)),
      pageSize: Math.min(100, Math.max(1, Math.floor(input.pageSize))),
    },
    sortOptions: buildSortOptions(input.sortFields),
    filterOptionGroup: filterOptions.length
      ? { logicalOperator: '', filterOptions, filterOptionGroups: [] }
      : null,
  };
}

function buildSortOptions(
  sortFields: SortField<PlatformChartListItemDto>[] | undefined,
): PlatformDataSourceQuery['sortOptions'] {
  const sorts = (sortFields ?? [])
    .filter(sortField => BACKEND_PROPERTY_BY_COLUMN_KEY[sortField.key])
    .map(sortField => ({
      propertyName: BACKEND_PROPERTY_BY_COLUMN_KEY[sortField.key],
      direction: sortField.direction,
    }));
  return sorts.length ? sorts : [HD_CHART_DEFAULT_SORT];
}

function buildFilterOptions(input: HdChartQueryInput): PlatformFilterOption[] {
  const filterOptions: PlatformFilterOption[] = [];
  const search = input.search?.trim();
  if (search) {
    filterOptions.push({
      propertyName: 'Name',
      operator: DsQueryFilterOperator.StringContains,
      value: search,
    });
  }
  for (const filterField of input.filterFields ?? []) {
    const filterOption = toFilterOption(filterField);
    if (filterOption) filterOptions.push(filterOption);
  }
  for (const [listKey, propertyName] of Object.entries(PROPERTY_FILTER_BACKEND_PROPERTY)) {
    const listValue = input.propertyFilters?.[listKey as 'hdTypes' | 'hdAuthorities' | 'hdDefinitions' | 'hdProfiles'];
    const values = listValue?.filter(value => !!value) ?? [];
    const filterOption = toPropertyFilterOption(propertyName, values);
    if (filterOption) filterOptions.push(filterOption);
  }
  // Center-states per gezet center (Unknown/0 = niet filteren). Semantiek:
  // Defined (3) en Open (1) exact; Undefined (2) omvat óók Open — een open
  // center ís immers undefined — via een inclusieve range 1..2.
  for (const [propertyName, state] of Object.entries(input.propertyFilters?.centerStates ?? {})) {
    if (!state) continue;
    if (state === 2) {
      filterOptions.push({
        propertyName,
        operator: DsQueryFilterOperator.NumRangeBetween,
        value: JSON.stringify({ Start: 1, End: 2 }),
      });
    } else {
      filterOptions.push({
        propertyName,
        operator: DsQueryFilterOperator.IntEquals,
        value: `${state}`,
      });
    }
  }
  return filterOptions;
}

/**
 * 1 waarde → STRINGEQUALS; ≥2 → ORCOMBINATIONEQUALS met `values[]` (backend-contract:
 * 2–20 waarden, geen `;` in waarden — onze canonieke keys bevatten die nooit).
 */
function toPropertyFilterOption(propertyName: string, values: string[]): PlatformFilterOption | null {
  if (values.length === 0) return null;
  if (values.length === 1) {
    return { propertyName, operator: DsQueryFilterOperator.StringEquals, value: values[0] };
  }
  return { propertyName, operator: DsQueryFilterOperator.OrCombinationEquals, values, value: null };
}

function toFilterOption(filterField: FilterField<PlatformChartListItemDto>): PlatformFilterOption | null {
  const propertyName = BACKEND_PROPERTY_BY_COLUMN_KEY[filterField.key];
  const operator = FILTER_OPERATOR_BY_COLUMN_KEY[filterField.key];
  if (!propertyName || !operator) return null;
  return { propertyName, operator, value: `${filterField.value}` };
}
